import { clientLogging } from '@bfa/nextjs-common/services';
import { useRouter } from 'next/router';
import { useState } from 'react';
import Uri from 'urijs';

import { ABSOLUTE_URLS, SUBSCRIPTION_FLOWS } from '../../../constants';
import { GQLDExperiment, GQLProgram } from '../../../gql.generated';
import { setMembershipConfirmationCookies } from '../../../utils/cookies';
import {
  DEFAULT_ERROR_MESSAGE,
  GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS,
} from '../constants';
import useUpgradeGbx from '../graphql/hooks/useAddGbxSub';

const getConfirmationPage = (isNewMembershipPageInUse: boolean): string => {
  if (isNewMembershipPageInUse) return ABSOLUTE_URLS.MEMBERSHIP_CONFIRMATION;
  return ABSOLUTE_URLS.IGW_GBX_CONFIRMATION;
};

const useOrganicUpgradeToGbx = (
  isNewMembershipPageInUse: boolean,
  logAnalytics: (label: string) => any,
  paymentRetryEnabled: boolean,
  experimentId?: GQLDExperiment,
  isWithGiftForGbx?: boolean
) => {
  const router = useRouter();
  const [onUpgradeError, setOnUpgradeError] = useState(false);
  const [onUpgradeloading, setOnUpgradeloading] = useState(false);
  const [upgradeGbx, { loading, error }] = useUpgradeGbx(
    experimentId,
    isWithGiftForGbx
  );
  const { deferredEvents } = router.query as {
    deferredEvents: string;
  };

  /**
   * Handling GBX Upgrade.
   */
  async function handleUpgradeToGbx() {
    logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.CLICKED_CONFIRMED);
    setOnUpgradeloading(true);
    try {
      const { data: mutationData } = await upgradeGbx();
      const {
        error: errorTryingToUpgrade = true,
        errorMessage,
        isWithGift,
        isPaymentDecline,
      } = mutationData?.gbxUpgrade?.gbxUpgradeTransaction || {};
      setOnUpgradeError(errorTryingToUpgrade);
      if (errorTryingToUpgrade) {
        throw new Error(errorMessage ?? DEFAULT_ERROR_MESSAGE);
      }
      setMembershipConfirmationCookies(
        GQLProgram.GBX,
        isWithGift,
        SUBSCRIPTION_FLOWS.ORGANIC
      );
      logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.SUCCESS);
      if (paymentRetryEnabled && isPaymentDecline) {
        let redirectUrl = `${
          ABSOLUTE_URLS.PAYMENT_RETRY
        }?program=GBX&redirectUrl=${getConfirmationPage(
          isNewMembershipPageInUse
        )}`;
        if (deferredEvents) {
          redirectUrl = new Uri(redirectUrl)
            .addQuery('deferredEvents', deferredEvents)
            .href();
        }
        router.push(redirectUrl);
        return;
      }
      router.replace(getConfirmationPage(isNewMembershipPageInUse));
    } catch (mutationError) {
      logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.FAILED);
      clientLogging.logError(mutationError as Error);
      setOnUpgradeError(true);
      setOnUpgradeloading(false);
    }
  }

  return {
    handleUpgradeToGbx,
    loading: loading || onUpgradeloading,
    error: onUpgradeError || error,
  };
};

export default useOrganicUpgradeToGbx;
